/*
 * Purpose: Shared Super Intent entry — home hero + compose describe
 * Last modified: 2026-06-27 | Agent
 * Completeness: 95
 */
import { FormEvent, KeyboardEvent, useCallback } from 'react';
import { ArrowUp, Pencil, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { BrandMark } from '@/components/brand/BrandMark';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { AVERAGE_OUTCOME_SEEDS } from '@/seeds/averageOutcomes';
import { hasResolvedSecret } from '@/lib/resolveSecret';
import { PRODUCT_TERMS, PRODUCT_BRAND } from '@/lib/productTerms';

export type SuperIntentVariant = 'home' | 'compose';

interface SuperIntentInterpreterProps {
  variant?: SuperIntentVariant;
  value: string;
  onChange: (text: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  error?: string | null;
  showExamples?: boolean;
  className?: string;
}

const EXAMPLE_OUTCOMES = AVERAGE_OUTCOME_SEEDS.map((s) => s.outcomeText);

export function SuperIntentInterpreter({
  variant = 'compose',
  value,
  onChange,
  onSubmit,
  disabled,
  isLoading,
  error,
  showExamples = variant === 'compose',
  className,
}: SuperIntentInterpreterProps) {
  const llmReady = hasResolvedSecret('OPENAI_API_KEY');
  const isHome = variant === 'home';

  const handleSubmit = useCallback(
    (e?: FormEvent) => {
      e?.preventDefault();
      if (!value.trim() || disabled || isLoading) return;
      onSubmit();
    },
    [value, disabled, isLoading, onSubmit]
  );

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  if (isHome) {
    return (
      <form onSubmit={handleSubmit} className={cn('relative w-full max-w-2xl overflow-visible', className)}>
        <div className="ab-home-input-shell relative flex items-end gap-2 rounded-3xl border border-border/80 bg-card/90 backdrop-blur-md shadow-lg px-3 py-2 sm:px-4 sm:py-3">
          <BrandMark size={28} className="shrink-0 mb-1.5 opacity-90 hidden sm:block" />
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={`Ask ${PRODUCT_BRAND}…`}
            rows={1}
            disabled={disabled || isLoading}
            className="ui-form-control flex-1 min-h-[2.75rem] max-h-32 resize-none border-0 shadow-none bg-transparent focus-visible:ring-0 text-base sm:text-lg placeholder:text-muted-foreground/70"
          />
          <Button
            type="submit"
            size="icon"
            disabled={!value.trim() || disabled || isLoading}
            className="shrink-0 rounded-full h-10 w-10 ui-lift ui-lift-accent mb-0.5"
            aria-label="Start Mission"
          >
            <ArrowUp className="h-5 w-5" />
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkles className="h-5 w-5 text-primary" />
        <h2 className="text-lg sm:text-xl font-semibold">Describe your outcome</h2>
        {llmReady && (
          <Badge variant="default" className="text-[10px]">
            AI planner ready
          </Badge>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        Tell {PRODUCT_BRAND} what you want — not how to build it. The {PRODUCT_TERMS.autoBlocksFactory}{' '}
        assembles an {PRODUCT_TERMS.autoBlockFlow}; while running it&apos;s a Mission.
      </p>
      <Textarea
        placeholder="e.g. When LinkedIn emails me jobs I care about, tailor my resume, apply, and track everything."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        rows={4}
        disabled={disabled || isLoading}
        className="min-h-[100px] text-base"
      />
      {showExamples && (
        <div className="flex flex-wrap gap-2">
          {EXAMPLE_OUTCOMES.map((ex) => (
            <button
              key={ex}
              type="button"
              onClick={() => onChange(ex)}
              className="text-left text-xs sm:text-sm px-3 py-2 rounded-md border border-border hover:border-primary/50 hover:bg-muted/50 transition-colors"
            >
              {ex.length > 72 ? `${ex.slice(0, 72)}…` : ex}
            </button>
          ))}
        </div>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button
        onClick={() => handleSubmit()}
        disabled={!value.trim() || disabled || isLoading}
        className="w-full sm:w-auto"
      >
        <Pencil className="h-4 w-4 mr-2" />
        {isLoading ? 'Interpreting…' : 'Interpret outcome'}
      </Button>
    </div>
  );
}
